// Shared severity/category/sort helpers for every findings list surface. Extracted so
// FindingsList (and anything embedding it) has exactly one copy of this logic — previously each
// bespoke findings surface declared near-identical severity/category maps independently.
import type {
  Finding,
  FindingCategory,
  FindingSeverity,
  FindingStatus,
} from "@/lib/api/building";
import { criterionLabel } from "@/lib/config-completeness";

export const SEVERITY_STYLE: Record<
  FindingSeverity,
  { dot: string; border: string; badge: string; label: string }
> = {
  critical: {
    dot: "bg-destructive",
    border: "border-l-destructive",
    badge: "bg-destructive/10 text-destructive",
    label: "Kriittinen",
  },
  warning: {
    dot: "bg-warning",
    border: "border-l-warning",
    badge: "bg-warning-subtle text-warning-subtle-foreground",
    label: "Varoitus",
  },
  info: {
    dot: "bg-primary/60",
    border: "border-l-primary/60",
    badge: "bg-primary/10 text-primary",
    label: "Huomio",
  },
};

export const CATEGORY_STYLE: Record<
  FindingCategory,
  { badge: string; label: string }
> = {
  fault: { badge: "bg-destructive/10 text-destructive", label: "Laitevika" },
  comfort: {
    badge: "bg-warning-subtle text-warning-subtle-foreground",
    label: "Olosuhde",
  },
  data_quality: { badge: "bg-primary/10 text-primary", label: "Datalaatu" },
  opportunity: { badge: "bg-success/10 text-success", label: "Mahdollisuus" },
  configuration: {
    badge: "bg-info-subtle text-info-subtle-foreground",
    label: "Konfiguraatio",
  },
};

export const SOURCE_TYPE_LABEL: Record<Finding["source_type"], string> = {
  equipment: "Talotekniikka",
  zone: "Tila",
  building: "Kiinteistö",
};

// Entity-label fact for the collapsed-row subline — adapts ActivityFeed's `row.where.label`
// subline fact (docs/spec/terminology.md#equipment-talotekniikka) so a Talotekniikka/Tila row
// names the equipment/zone instance, not just its type. Null when the source has no single label
// (building-scoped conflict finding) or was deleted — never fall back to the raw source_id (no
// UUIDs in the UI). Also null when `scopedSourceId` already matches the finding's own source —
// the embed (equipment/zone detail panel) already implies that one item, so repeating its name
// would be redundant, the same idea as #1321 omitting the building name on a single-building feed.
export function findingSourceLabelFact(
  finding: Pick<Finding, "source_id" | "source_label">,
  scopedSourceId?: string,
): string | null {
  if (scopedSourceId && finding.source_id === scopedSourceId) return null;
  return finding.source_label;
}

export const STATUS_LABEL: Record<FindingStatus, string> = {
  open: "Avoin",
  acknowledged: "Kuitattu",
  resolved: "Ratkaistu",
};

// "Not resolved" — the default scope for an embedded detail-panel finding list (equipment, zone,
// data-point history), which never had a status toggle before and hid only resolved findings.
export const OPEN_AND_ACKNOWLEDGED: FindingStatus[] = ["open", "acknowledged"];

// Every status — for surfaces (e.g. data-point history) whose count/markers cover all findings
// regardless of status, so the list must match rather than silently narrowing what they show.
export const ALL_STATUSES: FindingStatus[] = [
  "open",
  "acknowledged",
  "resolved",
];

// Severity rank for sorting — lower sorts first (most urgent first).
const SEVERITY_ORDER: Record<FindingSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

// Open-first, severity-ordered, then most-recently-opened first — the ordering every findings
// surface (portfolio feed, building Optimointi, /findings) is meant to share.
export function compareFindings(a: Finding, b: Finding): number {
  const statusDiff = statusRank(a.status) - statusRank(b.status);
  if (statusDiff !== 0) return statusDiff;
  const sevDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  if (sevDiff !== 0) return sevDiff;
  return new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime();
}

export function sortFindings(findings: Finding[]): Finding[] {
  return [...findings].sort(compareFindings);
}

/** One accordion per Finnish Havainto model so types are not crammed into one list. */
export type FindingStack = {
  main: Finding;
  related: Finding[];
};

function stackKey(finding: Finding): string {
  return `${finding.finding_type}\0${findingModelLabel(finding)}`;
}

/**
 * Group by the Finnish model title (and `finding_type`, so two checks that happen to share a
 * word stay apart). A singleton stays a normal row (`related` empty). Two or more of the same
 * model share one collapsed header (`stackedFindingsHeader`) and list members inside.
 */
export function stackRelatedFindings(findings: Finding[]): FindingStack[] {
  if (findings.length === 0) return [];
  const buckets = new Map<string, Finding[]>();
  for (const finding of findings) {
    const key = stackKey(finding);
    const group = buckets.get(key) ?? [];
    group.push(finding);
    buckets.set(key, group);
  }
  return [...buckets.values()]
    .map((group) => {
      const sorted = sortFindings(group);
      return { main: sorted[0], related: sorted.slice(1) };
    })
    .sort((a, b) => compareFindings(a.main, b.main));
}

/** Collapsed-row id to open for a deep-linked finding — the stack main, even when `findingId` is a member. */
export function stackMainIdForFinding(
  stacks: FindingStack[],
  findingId: string,
): string | null {
  for (const stack of stacks) {
    if (stack.main.id === findingId) return stack.main.id;
    if (stack.related.some((member) => member.id === findingId)) {
      return stack.main.id;
    }
  }
  return null;
}

function sentenceCaseFi(label: string): string {
  if (!label) return label;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Quality writer suffix: `name_fi: signal` — a short name, not a measured-fact clause. */
function isSignalSuffix(suffix: string): boolean {
  if (!suffix) return false;
  if (suffix.includes(". ")) return false;
  return suffix.length <= 80;
}

/**
 * Collapsed-row title: the Finnish model name, never a raw `finding_type` id and never the
 * instance suffix after `:`. Configuration uses the shared completeness criterion label;
 * quality rows use the KB check heading before the signal; other categories keep the
 * observation when it is already the measured-fact sentence.
 */
export function findingModelLabel(
  finding: Pick<Finding, "observation" | "finding_type" | "category">,
): string {
  if (finding.category === "configuration") {
    const fromId = criterionLabel(finding.finding_type);
    if (fromId !== finding.finding_type) return sentenceCaseFi(fromId);
  }
  const observation = finding.observation.trim();
  const colon = observation.lastIndexOf(":");
  if (colon > 0) {
    const heading = observation.slice(0, colon).trim();
    const suffix = observation.slice(colon + 1).trim();
    if (heading && isSignalSuffix(suffix)) return heading;
  }
  return observation.replace(/\.$/, "").trim() || "Havainto";
}

/** Signal / instance clause after the model heading, or null when the observation is the model. */
export function findingObservationDetail(
  finding: Pick<Finding, "observation" | "finding_type" | "category">,
): string | null {
  const observation = finding.observation.trim();
  const colon = observation.lastIndexOf(":");
  if (colon <= 0) return null;
  const heading = observation.slice(0, colon).trim();
  const detail = observation.slice(colon + 1).trim();
  if (!heading || !detail || !isSignalSuffix(detail)) return null;
  return detail;
}

/** Inside a stack: the instance (signal or equipment), not a repeat of the model title. */
export function findingMemberTitle(
  finding: Pick<
    Finding,
    "observation" | "finding_type" | "category" | "source_label"
  >,
): string {
  return (
    findingObservationDetail(finding) ??
    finding.source_label ??
    findingModelLabel(finding)
  );
}

function sourceTypeFact(
  finding: Pick<Finding, "source_type" | "source_id" | "source_label">,
  scopedSourceId?: string,
): string {
  const sourceLabelFact = findingSourceLabelFact(finding, scopedSourceId);
  return sourceLabelFact
    ? `${SOURCE_TYPE_LABEL[finding.source_type]} · ${sourceLabelFact}`
    : SOURCE_TYPE_LABEL[finding.source_type];
}

function statusAndTimeFacts(
  finding: Pick<Finding, "status" | "opened_at" | "resolved_at">,
): string[] {
  const parts: string[] = [];
  if (finding.status !== "open") parts.push(STATUS_LABEL[finding.status]);
  parts.push(findingTimeFi(finding));
  return parts;
}

/** Collapsed subline: optional signal · source · time. */
export function findingCollapsedFacts(
  finding: Finding,
  scopedSourceId?: string,
  includeDetail = true,
): string {
  const parts: string[] = [];
  if (includeDetail) {
    const detail = findingObservationDetail(finding);
    if (detail) parts.push(detail);
  }
  parts.push(sourceTypeFact(finding, scopedSourceId));
  parts.push(...statusAndTimeFacts(finding));
  return parts.join(" · ");
}

/** Stack collapsed subline: shared signal/source only when every member agrees; else type · time. */
export function stackCollapsedFacts(
  members: Finding[],
  scopedSourceId?: string,
): string {
  if (members.length === 0) return "";
  const main = members[0];
  const parts: string[] = [];
  const details = members.map(findingObservationDetail);
  if (details[0] && details.every((detail) => detail === details[0])) {
    parts.push(details[0]);
  }
  const types = new Set(members.map((member) => member.source_type));
  if (types.size === 1) {
    const labels = members.map((member) =>
      findingSourceLabelFact(member, scopedSourceId),
    );
    const sharedLabel = labels.every((label) => label === labels[0])
      ? labels[0]
      : null;
    const typeLabel = SOURCE_TYPE_LABEL[main.source_type];
    parts.push(sharedLabel ? `${typeLabel} · ${sharedLabel}` : typeLabel);
  }
  parts.push(...statusAndTimeFacts(main));
  return parts.join(" · ");
}

/** Collapsed stack title: the Havainto model, plus a count when the list has more than one. */
export function stackedFindingsHeader(members: Finding[]): string {
  if (members.length === 0) return "";
  const title = findingModelLabel(members[0]);
  if (members.length === 1) return title;
  return `${title} · ${members.length}`;
}

// fi-FI day/month hour:minute — same pattern as OptimizationBuildingSection's `timeFi`, so a
// timestamp reads the same way across every Havainnot-adjacent surface.
function timeFi(iso: string): string {
  return new Date(iso).toLocaleString("fi-FI", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Collapsed-row timestamp: a still-open finding shows when it started; once `resolved_at` is
// set (resolved, or an acknowledged finding the operator already closed out) the row shows the
// full opened_at–resolved_at range instead of a single instant, per the packet's Done when.
export function findingTimeFi(
  finding: Pick<Finding, "opened_at" | "resolved_at">,
): string {
  if (finding.resolved_at) {
    return `${timeFi(finding.opened_at)}–${timeFi(finding.resolved_at)}`;
  }
  return timeFi(finding.opened_at);
}

function statusRank(status: FindingStatus): number {
  return status === "open" ? 0 : status === "acknowledged" ? 1 : 2;
}

// Shared sentence for the entity-kind FilterChipRow in FindingsList (/findings),
// RecommendationFeed (/recommendations), and ActivityFeed (Tapahtumaloki). Same axis on all
// three Toiminnot: whether the row is about signals, equipment, or zones — not a building
// ("kohde") and not a physical Sijainti.
export const ENTITY_KIND_FILTER_LABEL = "Suodata: mitä tämä koskee?" as const;

// Status chips on FindingsList (Avoin / Kuitattu / Kaikki). Same "Suodata:" sentence shape as
// the entity-kind row so the two groups read as parallel filters, not an unlabeled chip strip.
export const FINDING_STATUS_FILTER_LABEL =
  "Suodata: missä tilassa tämä on?" as const;

// Caption block for the expanded finding row — adapts CapabilityProse's Havainto/Selitys/
// Toimenpide captions (KbCapabilityCard.tsx) into a plain data shape so FindingRowItem can
// render them without importing the admin card.
export interface FindingProseBlock {
  caption: string;
  text: string;
}

export function findingProseBlocks(
  finding: Pick<Finding, "observation" | "explanation" | "remedy">,
): FindingProseBlock[] {
  return [
    { caption: "Havainto", text: finding.observation },
    { caption: "Selitys", text: finding.explanation?.trim() ?? "" },
    { caption: "Toimenpide", text: finding.remedy?.trim() ?? "" },
  ];
}
